import { type Control, type ILaneConfig, LaneConfig, type StoreControl, type TelemetryDataModel, type TelemetryDataValues, ZoomSliderMode } from 'commons';
import { sub } from 'date-fns';
import { getMappingKey } from '../../state_utils';
import type { DataTable } from './DataTable';
import type { MSDatModel } from './MSDatModel';

/**
 *
 * @param toRemove
 * @param leftOverLanes
 * @returns
 */
export function findControlDifference(
  toRemove: ILaneConfig | LaneConfig | undefined,
  leftOverLanes: ILaneConfig[] | LaneConfig[]
): (value: Control[], index: number) => Control[] {
  return (tableControls) =>
    tableControls.filter(
      (control) => toRemove?.controls.some(isSameControlAs(control)) && !leftOverLanes.flat().some(({ controls }) => controls.some(isSameControlAs(control)))
    );
}
/**
 *  Returns a function that takes a control and returns true if the control id matches the id passed in.
 * @param control
 * @returns
 */
export const isSameControlAs =
  ({ id }: { id: string | number }) =>
  ({ id: remove_id }: { id: string | number }) =>
    remove_id === id;
/**
 * Returns a function that takes a control and returns true if the control id matches the id passed in.
 * @param param0
 * @returns
 */
export const isIdLikeControl =
  ({ id: remove_id }: { id: string | number }) =>
  (id: string | number) =>
    remove_id === id;
/**
 * Returns a function that takes a control and returns true if the control id matches the id passed in.
 * @param id
 * @returns
 */
export const filterNotMatchingId =
  (id: string | number) =>
  ({ id: remove_id }: { id: string | number }) =>
    remove_id !== id;

/**
 *
 * @returns
 */
export function getDefaultState(): MSDatModel {
  return {
    date_range: {
      start_date: sub(new Date(), { months: 1 }),
      end_date: new Date(),
    },
    comments: [],
    zoomSliderMode: ZoomSliderMode.GLOBAL,
    isLoading: [],
    laneHistoryCount: 1,
    activeDatasources: [],
    activeNodes: [],
    headers: [],
    uploaded_files: [],
    controls: [],
    hidden_controls: [],
    dataTable: {},
    kpiTable: {},
    views: [],
    zoom: [
      {
        start: 0,
        end: 100,
        startValue: undefined,
        endValue: undefined,
        axis: ['0'],
        dataZoomId: 'zoom_0_0',
      },
    ],
    lanes: [new LaneConfig()],
    echart_config: {
      series: [
        {
          id: 'a',
          type: 'line',
          smooth: true,
          symbolSize: 1,
          data: [],
        },
      ],
    },
  };
}
/**
 *
 * @param param0
 * @returns
 */
export function mergeAndUpdateArrays({ org, newL }: { org: TelemetryDataValues[]; newL: TelemetryDataValues[] }): TelemetryDataValues[] {
  if (!org) {
    return newL;
  }
  return org
    .map((obj1) => {
      const obj2 = newL.find((o) => o.datetime === obj1.datetime);
      return obj2 ? { ...obj1, ...obj2 } : obj1;
    })
    .concat(newL.filter((obj2) => !org.some((obj1) => obj1.datetime === obj2.datetime)));
}
/**
 *
 * @param control
 * @param dataTable
 * @returns
 */
export function updateDataTable(control: StoreControl, dataTable: DataTable) {
  return (newData: TelemetryDataModel) => {
    const mapDT = getMappingKey(control.id, newData);

    // Filter out the mapDT key from the dataTable object
    // and set the control.label property to undefined for each item in the arrays.
    const updatedDataTable = Object.keys(dataTable)
      .filter((key) => key !== mapDT)
      .reduce(
        (accumulator, currentKey) => ({
          ...accumulator,
          [currentKey]: dataTable[currentKey].map((item) => ({
            ...item,
            [control.raw_label]: undefined,
          })),
        }),
        {}
      );

    // Ret
    // Return the updatedDataTable object with the merged array for mapDT
    return {
      ...updatedDataTable,
      [mapDT]: mergeAndUpdateArrays({
        org: dataTable[mapDT],
        newL: newData.channelValues.flatMap((data) => data.values),
      }),
    };
  };
}
